import { UnitSystem } from 'forms/my-account'
import { PERSON_DOB_API_FORMAT, PERSON_DOB_UI_FORMAT } from 'constants/date-options'
import {
  CALENDAR_DATE_FORMAT,
  MIN_AGE_REFERENCE,
  MIN_GAMES_TO_WIN,
  MIN_TBPOINTS_TO_WIN,
  NO_SCORE_MATCHES,
  OPTED_OUT_PLAYER_NAME,
} from 'constants/profile'
import i18next from 'i18next'
import { HeightBreakdown } from 'types/forms/my-account'
import {
  AddressUI,
  CsvColumnConfig,
  CustomEventWtn,
  ErrorWithContext,
  PersonIDEnum,
  PersonIDInput,
  Player,
  Sorting,
  SortingDirection,
  SortingField,
  StoredUser,
} from 'types/global'
import moment from 'moment'
import {
  ACTIVATION_PAGE_BASE_URL,
  HIGH_CONFIDENCE_THRESHOLD,
  REL_PATHS,
  WtnPage,
  createRelPath,
} from 'constants/global'
import { getRcUrl } from 'constants/api'
import queryString from 'query-string'
import { toast } from 'react-toastify'
import { jsonToCSV } from 'react-papaparse'
import Storage from 'modules/storage'
import { SignUpTypeEnum } from 'types/api/enums'
import api from 'api/graphql/graphql'
import avatarUploadAPI from 'api/rest/avatar'

export const getSignInUrl = (redirectUrl = window.location.href) =>
  `/account/signin/?returnUrl=${encodeURIComponent(redirectUrl)}`

export const getSignOutUrl = (redirectUrl = window.location.origin) =>
  `/account/signout/?returnUrl=${encodeURIComponent(redirectUrl)}`

export const getActivationPageUrl = () => ACTIVATION_PAGE_BASE_URL

export const getOwnProfileUrl = () =>
  `${window.location.origin}/eng/player-profile?own-profile=true`

export const getOwnProfileUrlWithoutOrigin = () => `player-profile?own-profile=true`

export const didBrowserNavigateBack = () =>
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type ===
  'back_forward'

export const didBrowserReload = () =>
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type === 'reload'

const FOOT_IN_METERS = 0.3048
const INCH_IN_METERS = 0.0254

export function zeroPadNumber(n: number) {
  return n < 10 ? '0' + n : n
}

export function formatTimestamp(date: Date): string {
  const months = i18next.t('monthsShort', { ns: 'strDates', returnObjects: true }) as string[]
  return (
    zeroPadNumber(date.getDate()) +
    ' ' +
    months[date.getMonth()] +
    ' ' +
    zeroPadNumber(date.getFullYear()).toString().substr(-2)
  )
}

export function indexToDateChunk(index: number) {
  return index < 9 ? `0${index + 1}` : `${index + 1}`
}

export const splitFormattedDate = {
  getDay: (date: string | undefined) => {
    if (!date) return ''
    let arr = date.split('-')
    return arr.length === 3 ? arr[2] : ''
  },
  getMonth: (date: string | undefined) => {
    if (!date) return ''
    let arr = date.split('-')
    return arr.length === 3 ? arr[1] : ''
  },
  getYear: (date: string | undefined) => {
    if (!date) return ''
    let arr = date.split('-')
    return arr.length === 3 ? arr[0] : ''
  },
}

export const getFormattedDate = (day, month, year) => `${day}-${month}-${year}`

export function getHeightBreakdown<T extends UnitSystem>(
  heightInMeters: number,
  system: T
): HeightBreakdown<T> {
  if (system === UnitSystem.METRIC) {
    return {
      m: Math.floor(heightInMeters),
      cm: Math.round((heightInMeters * 100) % 100),
    } as HeightBreakdown<T>
  } else {
    const ft = Math.floor(heightInMeters / FOOT_IN_METERS)
    const inches = Math.floor((heightInMeters - ft * FOOT_IN_METERS) / INCH_IN_METERS)
    return { ft, inches } as HeightBreakdown<T>
  }
}

export function getHeightFromMetric(m: number, cm: number) {
  return (m * 100 + cm) / 100
}

export const getHeightFromImperial = (ft: number, inches: number) => {
  return Math.ceil((ft * FOOT_IN_METERS + inches * INCH_IN_METERS) * 100) / 100
}

export const initializeHeightValues = (height: number) => {
  return {
    heightInMeters: height || 0,
    ft: height ? getHeightBreakdown(height, UnitSystem.IMPERIAL).ft.toString() : '0',
    inches: height ? getHeightBreakdown(height, UnitSystem.IMPERIAL).inches.toString() : '0',
    m: height ? getHeightBreakdown(height, UnitSystem.METRIC).m.toString() : '0',
    cm: height ? getHeightBreakdown(height, UnitSystem.METRIC).cm.toString() : '0',
  }
}

export const capitalize = s => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const capitalizeEachWord = str => {
  return (
    str &&
    str.replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    })
  )
}

export const removeDuplicates = array => {
  return Array.from(new Set<any>(array))
}

export const nameAndWTN = (p: Player, contemporary = true) => {
  const wtn = contemporary ? p.contemporaryWtn : p.wtnSingles
  return `${p.fullName || i18next.t('noPlayerName', { ns: 'strData' })} (${
    wtn || i18next.t('na', { ns: 'strData' })
  })`
}

export const checkDatasetEmpty = dataset => {
  return dataset && !dataset.some(d => d.data.length > 0)
}

export const mapRange = (
  value: number,
  low1: number,
  high1: number,
  low2: number,
  high2: number
): number => {
  return low2 + ((high2 - low2) * (value - low1)) / (high1 - low1)
}

export const roundDownWtn = (
  wtn: number,
  options: {
    decimalPoints: number
  } = {
    decimalPoints: 1,
  }
): string =>
  (Math.round((wtn + Number.EPSILON) * 100) / 100).toFixed(options.decimalPoints + 1).slice(0, -1)

export const betweenRange = (value, a, b) => {
  var min = Math.min.apply(Math, [a, b]),
    max = Math.max.apply(Math, [a, b])
  return value >= min && value <= max
}

export const countDecimals = value => {
  if (Math.floor(value.valueOf()) === value.valueOf()) return 0
  return value.toString().split('.')[1].length || 0
}

export const removeUndefinedFields = obj => {
  let newObj = {}
  Object.keys(obj).forEach(key => {
    if (obj[key] === Object(obj[key])) newObj[key] = removeUndefinedFields(obj[key])
    else if (obj[key] !== undefined) newObj[key] = obj[key]
  })
  return newObj
}

export const getAgeReference = (age: number | undefined, birthYear: number | undefined): string => {
  let ageRef = ''
  if (typeof age !== 'number') return ageRef

  if (age < MIN_AGE_REFERENCE) {
    ageRef = `${i18next.t('under', { ns: 'strProfileHeader' })} ${MIN_AGE_REFERENCE}`
  } else if (age >= MIN_AGE_REFERENCE && age <= 19) {
    ageRef = `${i18next.t('bornIn', { ns: 'strProfileHeader' })} ${birthYear}`
  } else if (age > 19 && age <= 30) {
    ageRef = '20-30'
  } else if (age > 30 && age <= 40) {
    ageRef = '31-40'
  } else if (age > 40 && age <= 50) {
    ageRef = '41-50'
  } else if (age > 50) {
    ageRef = '50+'
  }

  return ageRef
}

export const isPlayerHidden = (player: Player) => {
  return (
    !!player.optedOutOfPublicDataConsent &&
    (player.firstName === OPTED_OUT_PLAYER_NAME || player.lastName === OPTED_OUT_PLAYER_NAME)
  )
}

export const sortPlayersAlphabetically = <T extends Player>(players: T[]): T[] =>
  players.sort((p1, p2) => {
    let namePlayerOne = p1.lastName ? p1.lastName : ''
    let namePlayerTwo = p2.lastName ? p2.lastName : ''

    if (namePlayerOne === namePlayerTwo) {
      namePlayerOne = p1.firstName ? p1.firstName : ''
      namePlayerTwo = p2.firstName ? p2.firstName : ''
      if (namePlayerOne === namePlayerTwo) return 0
    }

    return namePlayerOne.localeCompare(namePlayerTwo)
  })

export const isSetWon = (
  thisPlayerScore: number,
  opponentScore: number,
  options: {
    isTiebreaker?: boolean
    minTbPointsToWin?: number
    useTennisRules?: boolean
  } = { isTiebreaker: false, useTennisRules: false, minTbPointsToWin: MIN_TBPOINTS_TO_WIN }
): boolean => {
  const { isTiebreaker, useTennisRules } = options
  let minTbPointsToWin = options.minTbPointsToWin || MIN_TBPOINTS_TO_WIN

  if (!useTennisRules) return thisPlayerScore > opponentScore

  const advantage = thisPlayerScore - opponentScore

  if (isTiebreaker) return advantage > 1 && thisPlayerScore >= minTbPointsToWin

  return advantage > 1 && thisPlayerScore >= MIN_GAMES_TO_WIN
}

export const isNoScoreMatch = matchStatus => NO_SCORE_MATCHES.includes(matchStatus)

export const isFormattedDateValid = (date: string, format?: string) => {
  if (!date && typeof date !== 'string') return false
  const dateFormat = format ? format : PERSON_DOB_API_FORMAT
  return date.length === dateFormat.length && moment(date, dateFormat).isValid()
}

export const formatUiDate = (date: string, options?: { formatFrom?: string; formatTo: string }) => {
  const formatFrom = options ? options.formatFrom : PERSON_DOB_API_FORMAT
  const formatTo = options ? options.formatTo : PERSON_DOB_UI_FORMAT
  return moment(date, formatFrom).format(formatTo)
}
export const formatCalendarDate = (date?: string) => moment(date).format(CALENDAR_DATE_FORMAT)

export const splitAt = (str: string, char: string): string[] => {
  const chunksArr = str.split(char)
  const splitStr = chunksArr.map((chunk, i) => {
    if (i === chunksArr.length - 1) return chunk

    return chunk + char
  })

  return splitStr
}

export const isAnyChunkLonger = (str: string, limit: number): boolean => {
  if (!str) return false
  return str
    .replace('-', ' ')
    .split(' ')
    .some(chunk => chunk.length > limit)
}

export const clearMatchingLocalStorageEntries = (matcher: string, flag?: string) => {
  const regex = new RegExp(matcher, flag || 'i')
  if (localStorage) {
    const matchingKeys = Object.keys(localStorage).filter(key => regex.test(key))
    if (matchingKeys.length) {
      matchingKeys.forEach(key => localStorage.removeItem(key))
    }
  }
}

export const isWalkUpUser = (): boolean => {
  const { isWalkUpUser } = Storage.getRegistrationData() ?? {}
  return isWalkUpUser || false
}

export const isEmailAddressValid = (email: string): boolean => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  return regex.test(email)
}
export const transformLocalAssetUrl = (assetRelativePath: string) => getRcUrl() + assetRelativePath

export const getUnknownDisplayText = () => i18next.t('unknown', { ns: 'strData' })

export const hasCacheExpired = (timeCreated: number, cacheDuration: number) => {
  return Date.now() - timeCreated > cacheDuration
}

export const getProfileUrl = (id: string) =>
  createRelPath('PLAYER_PROFILE', { queryParams: { id } })

export const getQueryParam = (paramName: string) => {
  const value = queryString.parse(window.location.search)[paramName] || ''
  return typeof value === 'string' ? value : value[0] ?? ''
}

export const isPositiveInt = (str: string) => /^\d*$/.test(str)

export const getAllQueryParams = (): { [key: string]: string | undefined } => {
  const params = queryString.parse(window.location.search)
  for (let param in params) {
    const val = params[param]
    if (!val) {
      delete params[param]
    } else if (typeof val !== 'string') params[param] = val[0]
  }
  return params as { [key: string]: string | undefined }
}

export const removeParamFromHistory = (param: string) => {
  const url = new URL(window.location.href)
  url.searchParams.delete(param)
  window.history.replaceState(null, '', url.toString())
}

export const emitAppStart = function (user?: StoredUser) {
  window.dispatchEvent(
    new CustomEvent(CustomEventWtn.APP_START, {
      detail: {
        user,
      },
    })
  )
}

export const emitMenuOpening = (menuType: string) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventWtn.MENU_OPENING, {
      detail: {
        menuType,
      },
    })
  )
}

export const emitSuccessfulRegistration = function (type: SignUpTypeEnum) {
  window.dispatchEvent(
    new CustomEvent(CustomEventWtn.REGISTRATION, {
      detail: {
        type,
      },
    })
  )
}

export const isReferredByOther = () => {
  return document.referrer ? document.referrer.indexOf(window.location.origin) === -1 : false
}

export const isJunior = (player: Player): boolean =>
  typeof player.age === 'number' && player.age < 19

export const createDisplayDate = (
  d: Date,
  options?: { withYear?: boolean; withDay?: boolean; isMonthLongFormat?: boolean }
) => {
  const { withDay, withYear, isMonthLongFormat } = options || {}
  const daysShort = i18next.t('daysShort', { ns: 'strDates', returnObjects: true }) as string[]
  const monthsArray = i18next.t(isMonthLongFormat ? 'monthsLong' : 'monthsShort', {
    ns: 'strDates',
    returnObjects: true,
  }) as string[]

  return (
    (withDay ? `${daysShort[d.getUTCDay()]}, ` : '') +
    `${d.getUTCDate()} ${monthsArray[d.getUTCMonth()]}`
  )
}

export const copyToClipboard = (text: string) => {
  const notifyUser = (isCopySuccessful: boolean) =>
    isCopySuccessful
      ? toast.success(i18next.t('copiedToClipboard', { ns: 'strAlerts' }))
      : toast.error(i18next.t('failedCopyingToClipboard', { ns: 'strAlerts' }))

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text

    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.position = 'fixed'

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const isSuccessful = document.execCommand('copy')
      notifyUser(isSuccessful)
    } catch (err) {
      console.error('Failed to copy to clipboard', err)
      notifyUser(false)
    }

    document.body.removeChild(textArea)
  }

  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text)
    return
  }

  navigator.clipboard
    .writeText(text)
    .then(() => notifyUser(true))
    .catch(err => {
      console.error('Failed to copy to clipboard', err)
      notifyUser(false)
    })
}

export const formatAddress = (address: AddressUI): string => {
  const { city, state } = address

  const formattedCity = capitalizeEachWord(city)
  const formattedState = state.toUpperCase()

  const formatted = [formattedCity, formattedState].reduce((acc, data, i) => {
    if (acc && data) return `${acc} ${data}`

    return data || acc
  }, '')

  return formatted
}

export const createSortPlayersFunc = (sorting: Sorting) => (a: Player, b: Player) => {
  // We want players with no rating to be always at the bottom when sorting by rating
  const RATING_COMPARE_VALUE_FALLBACK = sorting.direction === SortingDirection.ASC ? 1000 : -1
  let compareField: keyof Player = 'lastName'
  let compareValueA: string | number
  let compareValueB: string | number

  if (sorting.field === SortingField.FamilyName) {
    compareValueA = a[compareField] ?? ''
    compareValueB = b[compareField] ?? ''
    return sorting.direction === SortingDirection.ASC
      ? compareValueA.localeCompare(compareValueB)
      : compareValueB.localeCompare(compareValueA)
  }
  compareField = 'wtnSinglesNumeric'

  if (sorting.field === SortingField.WTNDoubles) {
    compareField = 'wtnDoublesNumeric'
  }

  compareValueA = a?.[compareField] || RATING_COMPARE_VALUE_FALLBACK
  compareValueB = b?.[compareField] || RATING_COMPARE_VALUE_FALLBACK

  if (sorting.direction === SortingDirection.ASC) return compareValueA - compareValueB

  return compareValueB - compareValueA
}

export const downloadCsv = function (data: string, options: { filename?: string } = {}) {
  const { filename } = options
  const fullFilename = `${filename || 'download'}.csv`
  const blob = new Blob([data], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('download', fullFilename)
  a.click()
}

export const createCsv = <TItem>(data: TItem[], config: CsvColumnConfig<TItem>[]) => {
  const BOM = '\uFEFF' // This is needed for UTF-8 characters to be correctly decoded by Excel
  const jsonData: Record<string, string>[] = []

  data.forEach(item => {
    const jsonItem: Record<string, string> = {}
    config.forEach(({ header, key, transformValue }) => {
      jsonItem[header] = transformValue(item[key], item)
    })
    jsonData.push(jsonItem)
  })

  return (
    BOM + jsonToCSV({ fields: config.map(col => col.header), data: jsonData }, { quotes: true })
  )
}

export const setDisableBodyScroll = (shouldDisable: boolean) => {
  const nodes = [document.documentElement, document.body]
  const disabledScrollClass = 'mobile-nav-active'
  if (shouldDisable) {
    nodes.forEach(node => node.classList.add(disabledScrollClass))
  } else {
    nodes.forEach(node => node.classList.remove(disabledScrollClass))
  }
}

export function generateDateAtMidnight(
  options: { dateInput?: number | string; endOfDay?: boolean } = {}
) {
  const { endOfDay, dateInput } = options
  const hours: [number, number, number, number] = endOfDay ? [23, 59, 59, 999] : [0, 0, 0, 0]
  const date = dateInput ? new Date(dateInput) : new Date()
  date.setUTCHours(...hours)
  return date
}

export function isOdd(num: number) {
  return !!(num % 2)
}

export const isCurrentPageOneOf = (pages: WtnPage[]) =>
  pages.some(page => window.location.href.indexOf(REL_PATHS[page]) > -1)

export const extractClassicErrorMessage = async (response: Response): Promise<string> => {
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.indexOf('application/json') !== -1) {
    const { Message = '', ExceptionMessage = '' } = (await response.json()) ?? {}
    return ExceptionMessage || Message
  } else {
    return await response.text()
  }
}

export const getRayId = (response: Response): string | null => response.headers.get('cf-ray')

export const makeErrorWithContext = (
  response: Response,
  options: { message: string }
): ErrorWithContext => {
  const rayId = getRayId(response)
  const error = new Error(options.message)
  error['status'] = response.status
  error['rayId'] = rayId
  return error as ErrorWithContext
}

export const composeErrorMessage = (messages: string[]) => messages.filter(Boolean).join(': ')
export const isOfAge = (dob: string, minAge = 18) => {
  const today = new Date()
  const birthDate = new Date(dob)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age >= minAge
}

export function convertResidenceLongFormToAlpha3(countryOfResidenceCode: string) {
  const map = {
    'United States of America': 'USA',
  }
  return map[countryOfResidenceCode] || countryOfResidenceCode
}

export function convertResidenceAlpha3ToLongForm(countryOfResidenceCode: string) {
  const map = {
    USA: 'United States of America',
  }
  return map[countryOfResidenceCode] || countryOfResidenceCode
}

export const updateAvatar = async (avatarUrl: string) => {
  const res = await api.updateAccountDetails({ biographicalInformation: { avatarUrl } })
  return res.biographicalInformation.avatarUrl
}

export const uploadAvatar = async file => {
  const data = await avatarUploadAPI.uploadAvatar(file)
  await updateAvatar(data.ImageUrl)

  return data.ImageUrl
}

export function getProfileId(
  playerId: string | undefined | null,
  tennisId: string | undefined | null
): PersonIDInput {
  return {
    identifier: tennisId ? tennisId : (playerId as string),
    type: tennisId ? PersonIDEnum.TennisID : PersonIDEnum.ID,
  }
}

export const hasWtn = (profile: Player | null) => !!profile?.wtnSingles || !!profile?.wtnDoubles
export const hasAvatar = (profile: Player | null) => !!profile?.avatar
export const hasResidence = (profile: Player | null) => !!profile?.residence
export const playerInfoComplete = (profile: Player | null) => {
  const unknownValue = 'Unknown'
  const hasHeight = !!profile?.height
  const hasPreferredHand = !!profile?.preferredHand && profile?.preferredHand !== unknownValue
  const hasBackhand = !!profile?.backhand && profile?.backhand !== unknownValue
  const hasFavouriteShot = !!profile?.favouriteShot
  return hasHeight && hasPreferredHand && hasBackhand && hasFavouriteShot
}
export const hasSubmittedMatch = (profile: Player | null) => profile?.hasSubmittedResult
export const hasMatchHistory = (profile: Player | null) => !!profile?.totalMatchUps
export const hasPublishedMatches = (profile: Player | null) =>
  profile?.eligibleForSelfSubmission ? hasSubmittedMatch(profile) : hasMatchHistory(profile)
export const calculateCompletion = (profile: Player | null) => {
  let percentComplete = 0

  if (hasWtn(profile)) percentComplete += 25
  if (hasAvatar(profile)) percentComplete += 20
  if (hasResidence(profile)) percentComplete += 15
  if (playerInfoComplete(profile)) percentComplete += 15
  if (hasPublishedMatches(profile)) percentComplete += 25

  return percentComplete
}

export const isHighConfidence = (confidenceScore?: number) =>
  !!confidenceScore && confidenceScore > HIGH_CONFIDENCE_THRESHOLD
